import React, { useEffect, useId, useRef, useState } from 'react';
import './InfoPopover.css';

type InfoPopoverProps = {
  label: string;
  children: React.ReactNode;
  className?: string;
};

export default function InfoPopover({
  label,
  children,
  className = '',
}: InfoPopoverProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const closeOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', closeOnEscape);
    document.addEventListener('pointerdown', closeOutside);
    return () => {
      document.removeEventListener('keydown', closeOnEscape);
      document.removeEventListener('pointerdown', closeOutside);
    };
  }, [open]);

  return (
    <span
      ref={rootRef}
      className={`info-popover ${open ? 'info-popover--open' : ''} ${className}`.trim()}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="info-popover__trigger"
        aria-label={`Explain ${label}`}
        aria-expanded={open}
        aria-controls={open ? titleId : undefined}
        onClick={() => setOpen(true)}
        onFocus={() => setOpen(true)}
      >
        i
      </button>
      {open && (
        <span
          id={titleId}
          className="info-popover__content"
          role="dialog"
          aria-label={`${label} details`}
        >
          <strong className="info-popover__title">{label}</strong>
          {children}
        </span>
      )}
    </span>
  );
}
